import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface StreakData {
  current_streak: number;
  longest_streak: number;
  total_study_days: number;
}

export const useStudyStreak = () => {
  const { user } = useAuth();
  const [streak, setStreak] = useState<StreakData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStreak = async () => {
    if (!user) {
      setStreak(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_streaks')
        .select('current_streak, longest_streak, total_study_days')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      setStreak(data || { current_streak: 0, longest_streak: 0, total_study_days: 0 });
    } catch (error) {
      console.error('Error fetching streak:', error);
      setStreak({ current_streak: 0, longest_streak: 0, total_study_days: 0 });
    } finally {
      setLoading(false);
    }
  };

  const updateStreak = async () => {
    if (!user) return null;

    try {
      const { data, error } = await supabase.rpc('update_study_streak', {
        user_uuid: user.id
      });

      if (error) throw error;
      
      const result = data as unknown as StreakData;
      setStreak(result);
      return result;
    } catch (error) {
      console.error('Error updating streak:', error);
      return null;
    }
  };

  useEffect(() => {
    fetchStreak();
  }, [user]);

  return { streak, loading, updateStreak, refetch: fetchStreak };
};
